import {
  HttpStatusCode,
  type HttpClient
} from "@/core/services/protocols/types";
import { IFinancasGateway } from "./FinancasGateway";
import {
  IContasFinancas,
  IDREApi,
  IDREList,
  IEmpresasFinancas,
  IEmpresasCredenciadasParam,
  IEmpresasCredenciadas,
  IFinancasAuthResponse,
  IFluxoDeCaixa,
  IParmsFluxoDeCaixa,
  TDREApi,
  IParmsPMR,
  IPRMResponse,
  ICentroDeCusto,
  IUserProfileResponse,
  IUserRolesResponse,
  IFinancasDefaultResponse,
  TAdquirente,
  IresponseSolicitarCredenciamento,
  ICreateCredenciamentoPDV,
  TAPdvId,
  IPdvId,
  IEmpresasPDV,
  IEmpresasPDVResponse,
  IListPDV,
  IintegracoesPorPDV,
  IAdquirentesList,
  IAtualizarEtapa,
  ICieloSolicitarLinkReturn,
  ICreateCredenciamento,
  ICreateTaxas,
  ICredenciamentoEmpresasTable,
  ICredenciamentoId,
  IObterLinkCielo,
  IReturnAdquirentesList,
  IReturnAtualizarEtapa,
  IReturnEmpresasListByAdquirente,
  IReturnTableEmpresas,
  ISolicitarCredenciamento,
  IStatusTaxasReturn,
  ITaxaId,
  TAdquirentId,
  IParamsTreinamento,
  IReturnTreinamento,
  IParamsConfirmarAgendamento,
  IPlanosOrcamentariosFinancas
} from "./types";
import { UserProfile } from "./UserProfile";
import { UserRoles } from "./UserRoles";
import { injectable, inject, optional } from "inversify";
import { DITypes } from "@/core/@types/types";
import { IStatusPrimeiosPassos } from "@/views/financas/credenciamento/componentes/FluxosCredenciamento/shared/type";
import {
  FinancasDevHttpDi,
  type IFinancasDevHttp
} from "@/core/services/FinancasDevHttp";
import "reflect-metadata";

@injectable()
export default class FinancasHttpGateway implements IFinancasGateway {
  @inject(DITypes.FINANCAS_HTTP_CLIENT)
  @optional()
  private httpClient?: HttpClient;

  @inject(UserProfile)
  private userProfile!: UserProfile;

  @inject(UserRoles)
  private userRoles!: UserRoles;

  @inject(FinancasDevHttpDi)
  private financasDevHttp!: IFinancasDevHttp;

  async confirmarAgendamento(
    params: IParamsConfirmarAgendamento
  ): Promise<boolean> {
    const response = await this.httpClient?.post<IReturnTreinamento>({
      url: "TreinamentoAPI/ConfirmarAgendamento",
      body: {
        ...params
      }
    });

    if (!response?.body) return false;

    return true;
  }

  async listasTreinamento(): Promise<IReturnTreinamento> {
    const response = await this.httpClient?.get<IReturnTreinamento>({
      url: "TreinamentoAPI/BuscarTreinamentos"
    });

    if (!response?.body) return {};

    return response?.body;
  }

  async agendarReuniao(uri: string): Promise<boolean> {
    const response = await this.httpClient?.post<IReturnTreinamento>({
      url: "UsuarioAPI/ConfirmarAgendamentoKickOff",
      body: {
        uriCalendly: uri
      }
    });

    if (response?.statusCode != HttpStatusCode.success) return false;

    return true;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async mudarStatusTreinamento(params: IParamsTreinamento): Promise<any> {
    const response = await this.httpClient?.post<IReturnTreinamento>({
      url: "TreinamentoAPI/DefineTreinamento",
      body: { ...params }
    });

    if (!response?.body) return {};

    return response?.body;
  }

  async listarBoasVindas() {
    const response = await this.httpClient?.get<
      IStatusPrimeiosPassos | Record<string, boolean>
    >({
      url: "/UsuarioAPI/BuscarBoasVindas"
    });

    if (!response?.body) return {};

    return response?.body;
  }

  async ObterLinkCielo(params: IObterLinkCielo) {
    const response = await this.httpClient?.get<
      ICieloSolicitarLinkReturn | undefined
    >({
      url: "/CredenciamentoAdquirentesAPI/ObterUrlCielo",
      queryParams: { ...params }
    });

    if (!response?.body) return;

    return response?.body;
  }

  async AtualizarRede(
    params: ICredenciamentoId
  ): Promise<IresponseSolicitarCredenciamento | undefined> {
    const response = await this.httpClient?.post<
      IresponseSolicitarCredenciamento | undefined
    >({
      url: "/CredenciamentoAdquirentesAPI/AtualizarRede",
      body: { ...params }
    });

    if (!response?.body) return;

    return response?.body;
  }

  async SalvarTaxa(
    params: ICreateTaxas
  ): Promise<IStatusTaxasReturn | undefined> {
    const response = await this.httpClient?.post<
      IStatusTaxasReturn | undefined
    >({
      url: "/CredenciamentoAdquirentesAPI/SalvarTaxaV2",
      body: { ...params }
    });

    return response?.body;
  }

  async atualizarEtapa(
    params: IAtualizarEtapa
  ): Promise<IReturnAtualizarEtapa | undefined> {
    const response = await this.httpClient?.post<
      IresponseSolicitarCredenciamento | undefined
    >({
      url: "/CredenciamentoAdquirentesAPI/AtualizarEtapa",
      body: { ...params }
    });

    return response?.body;
  }

  async DeleteTaxa(params: ITaxaId): Promise<void> {
    await this.httpClient?.post<IresponseSolicitarCredenciamento | undefined>({
      url: "/CredenciamentoAdquirentesAPI/DeleteTaxa",
      body: { ...params }
    });
  }

  async getCredenciamento(
    params: ICredenciamentoId
  ): Promise<IresponseSolicitarCredenciamento | undefined> {
    const response = await this.httpClient?.get<
      IresponseSolicitarCredenciamento | undefined
    >({
      url: "/CredenciamentoAdquirentesAPI/GetCredenciamento",
      queryParams: { ...params }
    });

    if (!response?.body) return;

    return response?.body;
  }

  async solicitarCredenciamento(
    params: ISolicitarCredenciamento
  ): Promise<IresponseSolicitarCredenciamento | undefined> {
    const response = await this.httpClient?.post<
      IresponseSolicitarCredenciamento | undefined
    >({
      url: "/CredenciamentoAdquirentesAPI/SolicitarCredenciamento",
      body: { ...params }
    });

    return response?.body;
  }

  async getAdquirentesBandeirasList(): Promise<TAdquirente | undefined> {
    const response = await this.httpClient?.get<TAdquirente | undefined>({
      url: "/EnumListAPI/ListBandeiraDeCartao"
    });

    if (!response?.body) return;

    return response?.body;
  }

  async deletarCredenciamento(
    params: ICredenciamentoId
  ): Promise<string[] | undefined> {
    const response = await this.httpClient?.post<
      IFinancasDefaultResponse<string[]> | undefined
    >({
      url: "/CredenciamentoAdquirentesAPI/DeletarCredenciamento",
      body: { ...params }
    });

    if (!response?.body) return;

    return response?.body.Result;
  }

  async getEmpresasTable(
    params: ICredenciamentoEmpresasTable
  ): Promise<IReturnTableEmpresas | undefined> {
    const response = await this.httpClient?.get<
      IReturnTableEmpresas | undefined
    >({
      url: "CredenciamentoAdquirentesAPI/GetAdquirentesByEmpresa",
      queryParams: { ...params }
    });

    if (!response?.body) return;

    return response?.body;
  }

  async createCredenciamento(
    params: ICreateCredenciamento
  ): Promise<IFinancasDefaultResponse<TAdquirentId> | undefined> {
    const response = await this.httpClient?.post<
      IFinancasDefaultResponse<TAdquirentId> | undefined
    >({
      url: "/CredenciamentoAdquirentesAPI/CreateCredenciamento",
      body: { ...params }
    });

    return response?.body;
  }

  async getEmpresasByAdquirente(
    params: TAdquirentId
  ): Promise<IReturnEmpresasListByAdquirente | undefined> {
    const response = await this.httpClient?.get<
      IReturnEmpresasListByAdquirente | undefined
    >({
      url: "/CredenciamentoAdquirentesAPI/GetEmpresasByAdquirente",
      queryParams: { ...params }
    });

    if (!response?.body) return;

    return response?.body;
  }

  async getAdquirentesList(
    params: IAdquirentesList
  ): Promise<IReturnAdquirentesList | undefined> {
    const response = await this.httpClient?.get<
      IReturnAdquirentesList | undefined
    >({
      url: "/CredenciamentoAdquirentesAPI/GetAdquirentes",
      queryParams: { ...params }
    });

    if (!response?.body) return;

    return response?.body;
  }

  async getUrlLoginEduca(): Promise<string | undefined> {
    const response = await this.httpClient?.get<
      IFinancasDefaultResponse<string | undefined>
    >({
      url: "/DashboardAPI/ObterUrlDeLoginF360Educa/"
    });

    if (!response?.body?.Ok) return;

    return response?.body.Result;
  }

  async getZendeskToken(): Promise<string | undefined> {
    const response = await this.httpClient?.get<
      IFinancasDefaultResponse<string> | undefined
    >({
      url: "/UsuarioAPI/GetZendeskToken/"
    });

    return response?.body?.Result;
  }

  async getUserRoles(): Promise<UserRoles | undefined> {
    const response = await this.httpClient?.get<
      IFinancasDefaultResponse<IUserRolesResponse> | undefined
    >({
      url: "/UsuarioAPI/GetUserRoles/"
    });

    this.userRoles.response = response?.body?.Result;

    return this.userRoles;
  }

  async getUserProfile(): Promise<UserProfile | undefined> {
    const response = await this.httpClient?.get<
      IFinancasDefaultResponse<IUserProfileResponse> | undefined
    >({
      url: "/UsuarioAPI/GetCurrent/"
    });

    this.userProfile.response = response?.body?.Result;

    return this.userProfile;
  }

  async obterCentroDeCusto(): Promise<ICentroDeCusto | undefined> {
    const response = await this.httpClient?.get<ICentroDeCusto | undefined>({
      url: "/CentroDeCustoAPI/List/"
    });

    return response?.body;
  }

  async getObterLinhasDaDRE(id: string): Promise<TDREApi | undefined> {
    if (import.meta.env.DEV) {
      const response = await this.financasDevHttp.get(
        `/api/DREAPI/ObterLinhasDaDRE/?Id=${id}`
      );

      return {
        Result: response.Result,
        Ok: response.Ok || false
      };
    }

    const response = await this.httpClient?.get<TDREApi | undefined>({
      url: "DREAPI/ObterLinhasDaDRE/",
      queryParams: { Id: id }
    });

    return response?.body;
  }

  async auth(): Promise<IFinancasAuthResponse | undefined> {
    const response = await this.httpClient?.get<
      IFinancasAuthResponse | undefined
    >({
      url: "v2/auth"
    });

    return response?.body;
  }

  async fluxoDeCaixa(
    params: IParmsFluxoDeCaixa
  ): Promise<IFluxoDeCaixa | undefined> {
    const response = await this.httpClient?.get<IFluxoDeCaixa | undefined>({
      url: "/FluxoDeCaixaAPI/IniciarFluxoDeCaixa/",
      queryParams: params
    });

    return response?.body;
  }

  async getContas(): Promise<IContasFinancas | undefined> {
    const response = await this.httpClient?.get<IContasFinancas | undefined>({
      url: "/ContaAPI/ObterContasMin/"
    });

    return response?.body;
  }

  async getPlanosOrcamentarios(): Promise<IPlanosOrcamentariosFinancas | []> {
    const response = await this.httpClient?.get<
      IPlanosOrcamentariosFinancas | []
    >({
      url: "/PlanoOrcamentarioApi/ObterPlanosOrcamentariosMin/"
    });

    if (response?.statusCode !== 200 || !response?.body) return [];

    return response.body;
  }

  async getEmpresas(): Promise<IEmpresasFinancas | undefined> {
    if (import.meta.env.DEV) {
      const response = await this.financasDevHttp.get(
        `/api/EmpresaAPI/ObterEmpresasMin/`
      );

      return {
        Data: response.Data
      };
    }

    const response = await this.httpClient?.get<IEmpresasFinancas | undefined>({
      url: "/EmpresaAPI/ObterEmpresasMin/"
    });

    return response?.body;
  }

  async getEmpresasCredenciadas(
    params: IEmpresasCredenciadasParam
  ): Promise<IEmpresasCredenciadas | undefined> {
    const response = await this.httpClient?.get<
      IEmpresasCredenciadas | undefined
    >({
      url: "/CredenciamentoAdquirentesAPI/GetAllEmpresasCredenciadas",
      queryParams: params
    });

    return response?.body;
  }

  async getDreList(): Promise<IDREList | undefined> {
    if (import.meta.env.DEV) {
      const response = await this.financasDevHttp.get(
        `/api/DREAPI/List?sort=&group=&filter=`
      );

      return {
        Data: response.Data
      };
    }

    const response = await this.httpClient?.get<IDREList | undefined>({
      url: "DREAPI/List?sort=&group=&filter="
    });

    return response?.body;
  }

  async getPmrPagarReceber(
    params: IParmsPMR
  ): Promise<IPRMResponse | undefined> {
    const response = await this.httpClient?.get<IPRMResponse | undefined>({
      url: "/PlanoOrcamentarioAPI/PrazoMedioDeRecebimento/",
      queryParams: params
    });

    return response?.body;
  }

  async getDreApi(params: IDREApi): Promise<TDREApi | undefined> {
    if (import.meta.env.DEV) {
      const response = await this.financasDevHttp.post(`/api/DREAPI/ListDRE/`, {
        ...params
      });

      return {
        Result: response.Result,
        Ok: response.Ok || false
      };
    }

    const response = await this.httpClient?.post<TDREApi | undefined>({
      url: "DREAPI/ListDRE/",
      body: { ...params }
    });

    return response?.body;
  }

  async getListOperadoraDeCartao(): Promise<TAdquirente | undefined> {
    const response = await this.httpClient?.get<TAdquirente | undefined>({
      url: "/EnumListAPI/ListOperadoraDeCartao/"
    });

    return response?.body;
  }

  // credenciamento PDV
  async createCredenciamentoPDV(
    params: ICreateCredenciamentoPDV
  ): Promise<IFinancasDefaultResponse<TAPdvId> | undefined> {
    const response = await this.httpClient?.post<
      IFinancasDefaultResponse<TAPdvId> | undefined
    >({
      url: "/IntegracoesPDVsApi/AdicionarIntegracaoPDVDaEmpresa",
      body: { ...params }
    });

    return response?.body;
  }

  async getListPDVPaginado(
    params: IEmpresasPDV
  ): Promise<IEmpresasPDVResponse | undefined> {
    const response = await this.httpClient?.get<
      IEmpresasPDVResponse | undefined
    >({
      url: "/IntegracoesPDVsApi/ObterIntegracoesPDVsDasEmpresas",
      queryParams: params
    });

    return response?.body;
  }

  async getListPDVCriados(): Promise<IListPDV[] | undefined> {
    const response = await this.httpClient?.get<IListPDV[] | undefined>({
      url: "/IntegracoesPDVsApi/ListarPDVsDeEmpresaCriados"
    });

    return response?.body;
  }

  async getListPDV(): Promise<IListPDV[] | undefined> {
    const response = await this.httpClient?.get<IListPDV[] | undefined>({
      url: "/IntegracoesPDVsApi/ListarIntegracoesPdvDaEmpresa"
    });

    return response?.body;
  }

  async DeletePDV(params: IPdvId): Promise<void> {
    await this.httpClient?.post<IresponseSolicitarCredenciamento | undefined>({
      url: "/IntegracoesPDVsApi/RemoverIntegracaoPDVDaEmpresa",
      body: { ...params }
    });
  }

  async DeleteIntegracoesPorPDV(params: IintegracoesPorPDV): Promise<void> {
    await this.httpClient?.post<IresponseSolicitarCredenciamento | undefined>({
      url: "/IntegracoesPDVsApi/RemoverIntegracoesPorPDV",
      body: { ...params }
    });
  }
}
